import axios from 'axios'
import config from '../../../config'
import {
    OnboardStepResponse,
    OnboardStatusResponse,
    VerifyUniqueResponse,
    ApiErrorResponse,
    OnboardingUserDirectoryEntry,
} from '../types/types'
import { VerificationType } from '../constants/tenantOnboardEnums'

const { apiServerUrl } = config
const ONBOARD_BASE = `${apiServerUrl}/admin/tenant-onboard`

// ============================================================
// DEBUG: Error Simulation (set to true to test error flows)
// Remove before production!
// ============================================================
const SIMULATE_ERROR = false
const SIMULATE_ERROR_AT = 'CREATE_ADMIN_USER' // Which substep to fail at

// ============================================================
// Verify Uniqueness (Tenant Name / Admin Email)
// ============================================================

export const verifyUnique = async (
    type: VerificationType,
    value: string
): Promise<{
    available: boolean
    resourceId?: number
    error?: ApiErrorResponse
}> => {
    try {
        await axios.post<VerifyUniqueResponse>(`${ONBOARD_BASE}/verify`, {
            type,
            value,
        })
        return { available: true }
    } catch (err: any) {
        const status = err?.response?.status
        const data = err?.response?.data

        if (status === 409) {
            return {
                available: false,
                resourceId: data?.resourceId,
                error: {
                    message: data?.message || 'Already exists',
                    nextSteps: data?.nextSteps || [],
                    resourceId: data?.resourceId,
                },
            }
        }

        throw err
    }
}

// ============================================================
// Execute Onboarding Step
// ============================================================

export const executeOnboardStep = async (
    payload: Record<string, unknown>
): Promise<OnboardStepResponse> => {
    // --- Simulated Error (for testing) ---
    if (SIMULATE_ERROR && payload.substep === SIMULATE_ERROR_AT) {
        await new Promise((r) => setTimeout(r, 800)) // Simulate latency
        const simulatedError: ApiErrorResponse = {
            message: `[SIMULATED] Error at ${payload.substep}`,
            nextSteps: [
                'This is a simulated error for testing the Edit & Retry flow',
                'Change SIMULATE_ERROR to false in api.ts to disable',
            ],
        }
        // eslint-disable-next-line no-throw-literal
        throw simulatedError
    }

    try {
        const { data } = await axios.post<OnboardStepResponse>(
            `${ONBOARD_BASE}/`,
            payload
        )
        return data
    } catch (err: any) {
        const data = err?.response?.data
        if (data?.message) {
            const apiError: ApiErrorResponse = {
                message: data.message,
                nextSteps: data.nextSteps || [],
                resourceId: data.resourceId,
            }
            throw apiError
        }
        const networkError: ApiErrorResponse = {
            message: err.message || 'Network error',
            nextSteps: ['Check your network connection and try again'],
        }
        // eslint-disable-next-line no-throw-literal
        throw networkError
    }
}

// ============================================================
// Onboarding Users Directory
// ============================================================

export const getOnboardingUsersDirectory = async (
    search?: string
): Promise<OnboardingUserDirectoryEntry[]> => {
    const { data } = await axios.get<OnboardingUserDirectoryEntry[]>(
        `${ONBOARD_BASE}/onboarding-users`,
        { params: search ? { search } : {} }
    )
    return data
}

// ============================================================
// Get Onboard Status (for resume flow)
// ============================================================

export const getOnboardStatus = async (
    tenantId: number
): Promise<OnboardStatusResponse> => {
    const { data } = await axios.get<OnboardStatusResponse>(
        `${ONBOARD_BASE}/${tenantId}/status`
    )
    return data
}
